// IDs pages — NRB tree: County > Subcounty > Division > Location (Diaspora:
// Country > Embassy / consulate; Prisons: Prison).
//   IdsSection            headline figures, trend, coverage map
//   NotRegisteredSection  who holds an ID but isn't registered
//   SummaryMapTable       the small map + summary table (also on Home)

import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Grid, InputBase, LinearProgress, Typography } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SearchIcon from '@mui/icons-material/Search';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useMutation } from '@tanstack/react-query';
import { KenyaMap } from '@components/KenyaMap';
import { analyticsApi, AnalyticsPerson } from '@services/api/analytics';
import { areaName, dayLabelLong, periodLabels, useDataScope } from './data';
import {
  emptyNrb, ID_DEMO_LABELS, ID_MEASURE_LABELS, IdMeasure, IdRow, nrbChildLevel, nrbLabel, NrbFilter, NrbLevel, NRB_LEVELS,
  normalizeNrb, nrbVisibleLevels, TribeRow, TREND_LABELS, TrendBy, useIdPivot, useIdRows, useIdTotals, useIdTrend,
  useNrbOptions, useTribeSummary,
} from './agg';
import { IdDemoBy } from '@services/api/analytics';
import { PivotTable } from './PivotTable';
import { Cell } from 'recharts';
import {
  allDates, C, Column, DataTable, DATE_PRESETS, DateFilter, DateFilterValue, Dropdown, FilterBar, fmtShort, Kpi, Panel, Section, Toggle,
} from './ui';

/** 'county' -> 'counties', 'ward' -> 'wards', 'embassy / consulate' -> 'embassies / consulates'. */
const plural = (w: string) => w.split(' / ').map((x) => (/[^aeiou]y$/.test(x) ? `${x.slice(0, -1)}ies` : `${x}s`)).join(' / ');

const tooltipStyle = () => ({ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 });
/** The period in words: a preset reads "last 30 days to date"; only a custom range shows its dates. */
export const periodText = (d: DateFilterValue) => {
  if (d.preset === 'all') return 'all dates, to date';
  if (d.preset !== 'custom') {
    const p = DATE_PRESETS.find((x) => x.value === d.preset);
    if (p) return `${p.label.toLowerCase()} to date`;
  }
  return d.from === d.to ? dayLabelLong(d.from) : `${dayLabelLong(d.from)} – ${dayLabelLong(d.to)}`;
};

/** Coverage columns shared by the summary and IDs tables. */
function coverageColumns(levelLabel: string): Column<IdRow>[] {
  return [
    { key: 'name', label: levelLabel, value: (r) => areaName(r.name) },
    { key: 'ids', label: 'ID holders', value: (r) => r.ids, kind: 'num', color: C.blue },
    { key: 'registered', label: 'Registered', value: (r) => r.registered, kind: 'num', color: C.green },
    { key: 'notRegistered', label: 'Not registered', value: (r) => r.notRegistered, kind: 'num', color: C.orange },
    {
      key: 'pct', label: 'Coverage', value: (r) => r.pctRegistered, kind: 'pct', total: (rs) => {
        const i = rs.reduce((s, r) => s + r.ids, 0); return i ? (rs.reduce((s, r) => s + r.registered, 0) / i) * 100 : 0;
      },
    },
  ];
}

const ALL: DateFilterValue = { preset: 'all', from: '', to: '' };

const Loading: React.FC<{ on: boolean }> = ({ on }) =>
  on ? <LinearProgress sx={{ height: 2, backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: C.green } }} /> : null;

/** Small coverage map beside a summary table; clicking either focuses a county. */
export const SummaryMapTable: React.FC<{ county: string; onCounty: (c: string) => void; title?: string }> = ({ county, onCounty, title = 'Summary' }) => {
  const byCounty = useIdRows(emptyNrb, ALL);
  const sub = useIdRows({ ...emptyNrb, county }, ALL);
  const rows = county ? sub : byCounty;
  const level = county ? nrbLabel('subcounty', county) : 'County';
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Panel title="Coverage map" actions={county && (
          <Typography onClick={() => onCounty('')} sx={{ fontSize: '0.7rem', color: C.cyan, cursor: 'pointer' }}>Show all Kenya</Typography>
        )} sx={{ height: 460 }}>
          <Box sx={{ height: 380, p: 1 }}>
            <KenyaMap background={C.cardAlt} labelColor={C.muted}
              selectedCounty={county}
              onCountyClick={(c) => onCounty(c.name.toUpperCase() === county ? '' : c.name.toUpperCase())}
              countyData={byCounty.rows.map((r) => ({
                name: r.name, lat: 0, lng: 0, registered: r.registered, unregistered: r.notRegistered, percentage: r.pctRegistered,
              }))}
            />
          </Box>
          <Typography sx={{ fontSize: '0.65rem', color: C.muted, px: 1.5 }}>Share of ID holders registered · click a county to focus on it</Typography>
        </Panel>
      </Grid>
      <Grid item xs={12} md={8}>
        <Panel title={`${title} · ${areaName(county) || 'Kenya'} by ${level.toLowerCase()}`} sx={{ height: 460 }}>
          <Loading on={rows.fetching} />
          <DataTable rows={rows.rows} columns={coverageColumns(level)} rowKey={(r) => r.key} maxHeight={370}
            onRowClick={county ? undefined : (r) => onCounty(r.key)} fileName={`summary-${county || 'kenya'}`}
            searchPlaceholder={`Search ${level.toLowerCase()}…`} emptyText={rows.loading ? 'Loading…' : 'No rows'} />
        </Panel>
      </Grid>
    </Grid>
  );
};

/** Filter state shared by both ID pages: NRB area, ID number, date of registration. */
function useNrbFilters(county: string) {
  const [f, setF] = useState<NrbFilter>(normalizeNrb({ ...emptyNrb, county }));
  const scope = useDataScope();
  const [dates, setDates] = useState<DateFilterValue>(ALL);

  // follow the National overview county when it changes
  React.useEffect(() => setF(normalizeNrb({ ...emptyNrb, county })), [county]);

  const set = (lvl: NrbLevel, v: string) => {
    const i = NRB_LEVELS.indexOf(lvl);
    const next = { ...f, [lvl]: v };
    NRB_LEVELS.slice(i + 1).forEach((l) => { next[l] = ''; });
    setF(normalizeNrb(next));
  };
  const clear = () => { setF(emptyNrb); setDates(ALL); };
  const level = nrbChildLevel(f);
  const where = f.location || f.division || f.subcounty || f.county;
  const scopeName = f.county === 'PRISONS' && f.location ? f.location : areaName(where) || 'Kenya';
  /** Rows can be opened while the tree goes on below them. */
  const canDrill = !!level && (level === 'county' || nrbVisibleLevels(f.county).includes(level));
  const drill = (key: string) => { if (canDrill && level) set(level, key); };
  const dateValue: DateFilterValue = dates.preset === 'all' ? allDates(scope.first, scope.last) : dates;
  return { f, set, dates, dateValue, setDates, clear, level, scope: scopeName, drill, canDrill, first: scope.first, last: scope.last };
}

const AreaDropdown: React.FC<{ level: NrbLevel; f: NrbFilter; set: (l: NrbLevel, v: string) => void }> = ({ level, f, set }) => {
  const { values, loading } = useNrbOptions(level, f);
  const i = NRB_LEVELS.indexOf(level);
  const visible = nrbVisibleLevels(f.county);
  const above = visible[visible.indexOf(level) - 1];
  const ready = NRB_LEVELS.slice(0, i).every((l) => f[l]);
  const label = nrbLabel(level, f.county);
  return (
    <Dropdown label={label} value={f[level]} onChange={(v) => set(level, v)}
      options={values.map((v) => ({ value: v, label: areaName(v) }))}
      placeholder={!ready ? `Pick a ${nrbLabel(above || 'county', f.county).toLowerCase()} first` : loading ? 'Loading…' : `All ${plural(label.toLowerCase())}`}
      disabled={!ready} />
  );
};

/** ID number lookup: real status for one or many IDs. */
const IdLookup: React.FC = () => {
  const [ids, setIds] = useState('');
  const lookup = useMutation({ mutationFn: (s: string) => analyticsApi.lookup(s) });
  const res = lookup.data;
  const go = () => ids.trim() && lookup.mutate(ids);
  return (
    <>
      <Box sx={{ minWidth: 200 }}>
        <Typography sx={{ fontSize: '0.62rem', color: C.muted, fontWeight: 700, letterSpacing: 0.5, mb: 0.4 }}>ID NUMBER</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <InputBase value={ids} onChange={(e) => setIds(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go()}
            placeholder="ID or passport, or several"
            sx={{ fontSize: '0.8rem', color: C.text, backgroundColor: C.bg, border: `1px solid ${C.border}`, borderRadius: 1, px: 1, height: 40, flex: 1 }} />
          <Button onClick={go} disabled={!ids.trim() || lookup.isPending} variant="contained" sx={{ minWidth: 40, px: 1, backgroundColor: C.green, color: C.bg }}>
            {lookup.isPending ? <CircularProgress size={16} sx={{ color: C.bg }} /> : <SearchIcon fontSize="small" />}
          </Button>
        </Box>
      </Box>
      {(res || lookup.error) && (
        <Box sx={{ width: '100%' }}>
          {lookup.error && <Typography sx={{ color: C.orange, fontSize: '0.78rem' }}>{(lookup.error as any)?.message || 'Lookup failed'}</Typography>}
          {res && (
            <Panel title={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                ID lookup
                <Chip size="small" label={`${res.counts.registered} registered`} sx={{ height: 20, color: C.green, border: `1px solid ${C.green}66`, backgroundColor: 'transparent' }} />
                <Chip size="small" label={`${res.counts.unregistered} not registered`} sx={{ height: 20, color: C.orange, border: `1px solid ${C.orange}66`, backgroundColor: 'transparent' }} />
                <Chip size="small" label={`${res.counts.not_found} not found`} sx={{ height: 20, color: C.muted, border: `1px solid ${C.border}`, backgroundColor: 'transparent' }} />
              </Box>
            } actions={<Typography onClick={() => { lookup.reset(); setIds(''); }} sx={{ fontSize: '0.7rem', color: C.cyan, cursor: 'pointer' }}>Close</Typography>}
              sx={{ height: 'auto' }}>
              <DataTable<AnalyticsPerson> rows={res.results} rowKey={(p) => p.national_id} maxHeight={300} fileName="id-lookup" searchPlaceholder="Search ID…"
                columns={[
                  { key: 'id', label: 'ID number', value: (p) => p.national_id },
                  { key: 'status', label: 'Status', value: (p) => (!p.found ? 'Not found' : p.status === 'REGISTERED' ? 'Registered' : 'Not registered') },
                  { key: 'name', label: 'Name', value: (p) => p.full_name || '' },
                  { key: 'tribe', label: 'Tribe', value: (p) => p.tribe || '' },
                  { key: 'county', label: 'County', value: (p) => areaName(p.county || '') },
                  { key: 'sub', label: 'Subcounty', value: (p) => areaName(p.subcounty || '') },
                  { key: 'div', label: 'Division', value: (p) => areaName(p.division || '') },
                  { key: 'loc', label: 'Location', value: (p) => areaName(p.location || '') },
                  { key: 'pc', label: 'Polling station', value: (p) => (p.polling_centre ? `${p.polling_centre} (${p.ward})` : '') },
                  { key: 'date', label: 'Registered on', value: (p) => (p.registration_date ? p.registration_date + (p.registered_via_app ? ' (app)' : '') : '') },
                ]} />
            </Panel>
          )}
        </Box>
      )}
    </>
  );
};

const NrbFilterBar: React.FC<{ s: ReturnType<typeof useNrbFilters> }> = ({ s }) => (
  <>
    <FilterBar onClear={s.clear}>
      {nrbVisibleLevels(s.f.county).map((l) => <AreaDropdown key={l} level={l} f={s.f} set={s.set} />)}
      {s.first && <DateFilter label="Date of registration" value={s.dateValue} onChange={s.setDates} first={s.first} last={s.last} />}
      <IdLookup />
    </FilterBar>
    {s.dates.preset !== 'all' && (
      <Typography sx={{ fontSize: '0.7rem', color: C.muted, mt: -1, mb: 2 }}>
        Dates narrow registered voters (by registration date). ID holders not registered have no registration date and are counted as of today.
      </Typography>
    )}
  </>
);

const tribeColors = () => [C.green, C.blue, C.yellow, C.orange, C.purple, C.cyan, '#E8684A', '#6DC8EC', '#9270CA', '#FF9D4D', '#269A99', '#FF99C3', '#5B8FF9', '#8b95a5'];

/** Pivot (areas x tribe / sex / age group) and the per-tribe summary for
 *  ID holders under the filters. NRB records as loaded: dates don't apply. */
export const IdBreakdowns: React.FC<{ s: ReturnType<typeof useNrbFilters>; measure0: IdMeasure }> = ({ s, measure0 }) => (
  <TribeAndPivot f={s.f} scope={s.scope} level={s.level} onDrill={s.canDrill ? s.drill : undefined} measure0={measure0} />
);

/** Pivot (areas x tribe / sex / age group) and the per-tribe summary for
 *  ID holders under filter f, grouped by level (one total row when ''). */
export const TribeAndPivot: React.FC<{
  f: NrbFilter; scope: string; level: NrbLevel | ''; onDrill?: (key: string) => void; measure0: IdMeasure;
}> = ({ f, scope, level, onDrill, measure0 }) => {
  const s = { scope };
  const [by, setBy] = useState<IdDemoBy>('tribe');
  const [measure, setMeasure] = useState<IdMeasure>(measure0);
  const [tribeView, setTribeView] = useState('chart');
  const grouped = !!level;
  const pivot = useIdPivot(f, by, grouped);
  const tribes = useTribeSummary(f);
  const pick = (r: { registered: number[]; unregistered: number[] }) =>
    r.registered.map((x, j) => (measure === 'registered' ? x : measure === 'notRegistered' ? r.unregistered[j] : x + r.unregistered[j]));
  const rows = (pivot.pivot?.rows ?? []).map((r) => ({ key: r.key, label: areaName(r.key), values: pick(r) }));
  const rowHeader = level ? nrbLabel(level, f.county) : 'Area';
  const measureColor = measure === 'registered' ? C.green : measure === 'notRegistered' ? C.orange : C.blue;
  const sortedTribes = [...tribes.rows].sort((a, b) =>
    a.tribe === 'Not recorded' ? 1 : b.tribe === 'Not recorded' ? -1 : (measure === 'registered' ? b.registered - a.registered : measure === 'notRegistered' ? b.notRegistered - a.notRegistered : b.ids - a.ids));
  const tribeValue = (t: TribeRow) => (measure === 'registered' ? t.registered : measure === 'notRegistered' ? t.notRegistered : t.ids);
  const tribeTotal = sortedTribes.reduce((x, t) => x + tribeValue(t), 0);

  return (
    <>
      <Panel sx={{ height: 560, mb: 2 }}
        title={
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', py: 0.5 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mr: 1, mt: 2.5 }}>Pivot</Typography>
            <Dropdown label="Columns" value={by} clearable={false} minWidth={160}
              options={(['tribe', 'sex', 'age'] as IdDemoBy[]).map((k) => ({ value: k, label: ID_DEMO_LABELS[k] }))}
              onChange={(v) => setBy((v || 'tribe') as IdDemoBy)} />
            <Dropdown label="Show" value={measure} clearable={false} minWidth={170}
              options={(['ids', 'registered', 'notRegistered'] as IdMeasure[]).map((k) => ({ value: k, label: ID_MEASURE_LABELS[k] }))}
              onChange={(v) => setMeasure((v || 'ids') as IdMeasure)} />
            <Typography sx={{ fontSize: '0.68rem', color: C.muted, mt: 3 }}>
              {ID_MEASURE_LABELS[measure]} in {s.scope} by {rowHeader.toLowerCase()} and {ID_DEMO_LABELS[by].toLowerCase()} · from NRB records (all dates)
            </Typography>
          </Box>
        }>
        {pivot.fetching && <LinearProgress sx={{ height: 2, backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: measureColor } }} />}
        <PivotTable rowHeader={rowHeader} columns={pivot.pivot?.columns ?? []} rows={rows} maxHeight={430}
          onRowClick={onDrill} fileName={`ids-pivot-${by}-${measure}`} />
      </Panel>

      <Panel title={`By tribe · ${measure === 'ids' ? 'ID holders' : ID_MEASURE_LABELS[measure].toLowerCase()} in ${s.scope}`} sx={{ height: 480, mb: 2 }} actions={
        <Toggle value={tribeView} onChange={setTribeView} options={[{ value: 'chart', label: 'Chart' }, { value: 'table', label: 'Table' }]} />
      }>
        {tribes.fetching && <LinearProgress sx={{ height: 2, backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: measureColor } }} />}
        {tribeView === 'chart' ? (
          <Box sx={{ height: 420, p: 1 }}>
            <ResponsiveContainer>
              <BarChart data={sortedTribes.slice(0, 15).map((t) => ({ tribe: t.tribe, value: tribeValue(t) }))} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={fmtShort} tick={{ fill: C.muted, fontSize: 11 }} />
                <YAxis type="category" dataKey="tribe" width={110} tick={{ fill: C.text, fontSize: 11 }} interval={0} />
                <Tooltip contentStyle={tooltipStyle()} cursor={{ fill: C.faint }}
                  formatter={(v: number) => [`${v.toLocaleString()} (${(tribeTotal ? (v / tribeTotal) * 100 : 0).toFixed(1)}%)`, ID_MEASURE_LABELS[measure]]} />
                <Bar dataKey="value" radius={[0, 3, 3, 0]}>
                  {sortedTribes.slice(0, 15).map((t, i) => <Cell key={t.tribe} fill={t.tribe === 'Not recorded' ? C.muted : tribeColors()[i % tribeColors().length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <DataTable<TribeRow> rows={tribes.rows} rowKey={(r) => r.tribe} maxHeight={390} fileName="ids-by-tribe" searchPlaceholder="Search tribe…"
            columns={[
              { key: 'tribe', label: 'Tribe', value: (r) => r.tribe },
              { key: 'ids', label: 'ID holders', value: (r) => r.ids, kind: 'num', color: C.blue },
              { key: 'registered', label: 'Registered', value: (r) => r.registered, kind: 'num', color: C.green },
              { key: 'notRegistered', label: 'Not registered', value: (r) => r.notRegistered, kind: 'num', color: C.orange },
              {
                key: 'pct', label: 'Coverage', value: (r) => r.pct, kind: 'pct',
                total: (rs) => { const i = rs.reduce((x, r) => x + r.ids, 0); return i ? (rs.reduce((x, r) => x + r.registered, 0) / i) * 100 : 0; },
              },
            ]} />
        )}
      </Panel>
    </>
  );
};

/** IDs page: headline figures, registration trend, map + summary table. */
export const IdsSection: React.FC<{ county: string }> = ({ county }) => {
  const s = useNrbFilters(county);
  const { f, level } = s;
  const [by, setBy] = useState<TrendBy>('month');
  const { t, viaApp, loading } = useIdTotals(f, s.dates);
  const trend = useIdTrend(f, s.dates, by);
  const rows = useIdRows(f, s.dates);
  const byCounty = useIdRows(emptyNrb, s.dates);
  const levelName = level ? nrbLabel(level, f.county) : nrbLabel('location', f.county);
  const dash = loading ? '…' : '–';

  return (
    <Section id="ids" icon={<BadgeIcon />} title="IDs" accent={C.blue}
      subtitle={`${s.scope} · ID holders (NRB) and voters on the roll · registrations ${periodText(s.dates)}`}>
      <NrbFilterBar s={s} />

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Kpi label="ID holders" value={t ? t.ids : dash} color={C.blue} sub={`registered + not registered in ${s.scope}`} /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Registered voters" value={t ? t.registered : dash} color={C.green} sub={viaApp ? `${viaApp.toLocaleString()} via the mobile app` : 'on the IEBC voter roll'} /></Grid>
        <Grid item xs={6} md={3}><Kpi label="With IDs, not registered" value={t ? t.notRegistered : dash} color={C.orange} sub="the registration gap" /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Coverage" value={t ? `${t.pctRegistered.toFixed(1)}%` : dash} color={C.cyan} sub="of ID holders registered" /></Grid>
      </Grid>

      <Panel title="Registrations over time" sx={{ height: 'auto', mb: 2 }} actions={
        <Toggle value={by} onChange={(v) => setBy(v as TrendBy)} options={(['day', 'month', 'year'] as TrendBy[]).map((x) => ({ value: x, label: TREND_LABELS[x] }))} />
      }>
        <Loading on={trend.loading} />
        <Box sx={{ height: 260, p: 1 }}>
          <ResponsiveContainer>
            <BarChart data={((labels) => trend.points.map((p, i) => ({ ...p, label: labels[i] })))(periodLabels(trend.points.map((p) => p.period), by))} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: C.muted, fontSize: 11 }} minTickGap={16} interval="preserveStartEnd" />
              <YAxis tickFormatter={fmtShort} tick={{ fill: C.muted, fontSize: 11 }} width={48} />
              <Tooltip contentStyle={tooltipStyle()} cursor={{ fill: C.faint }} formatter={(v: number) => [v.toLocaleString(), 'Registered']} />
              <Bar dataKey="registered" name="Registered" fill={C.green} radius={[3, 3, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Panel>

      <IdBreakdowns s={s} measure0="ids" />

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Panel title="Coverage map" sx={{ height: 460 }}>
            <Box sx={{ height: 380, p: 1 }}>
              <KenyaMap background={C.cardAlt} labelColor={C.muted}
                selectedCounty={f.county}
                onCountyClick={(c) => s.set('county', c.name.toUpperCase() === f.county ? '' : c.name.toUpperCase())}
                countyData={byCounty.rows.map((r) => ({
                  name: r.name, lat: 0, lng: 0, registered: r.registered, unregistered: r.notRegistered, percentage: r.pctRegistered,
                }))}
              />
            </Box>
            <Typography sx={{ fontSize: '0.65rem', color: C.muted, px: 1.5 }}>Share of ID holders registered · click a county to filter</Typography>
          </Panel>
        </Grid>
        <Grid item xs={12} md={8}>
          <Panel title={`Summary · ${s.scope} by ${levelName.toLowerCase()}`} sx={{ height: 460 }}>
            <Loading on={rows.fetching} />
            <DataTable rows={rows.rows} columns={coverageColumns(levelName)} rowKey={(r) => r.key} maxHeight={370}
              onRowClick={s.canDrill ? (r) => s.drill(r.key) : undefined} fileName={`ids-${s.scope}`}
              searchPlaceholder={`Search ${levelName.toLowerCase()}…`} emptyText={rows.loading ? 'Loading…' : 'No rows'} />
          </Panel>
        </Grid>
      </Grid>
    </Section>
  );
};

/** With IDs, not registered: filters down to location, ID number, date. */
export const NotRegisteredSection: React.FC<{ county: string }> = ({ county }) => {
  const s = useNrbFilters(county);
  const { f, level } = s;
  const rows = useIdRows(f, s.dates);
  const totalNotReg = rows.rows.reduce((t, r) => t + r.notRegistered, 0);
  const drill = (r: IdRow) => s.drill(r.key);
  const levelName = level ? nrbLabel(level, f.county) : nrbLabel('location', f.county);

  const idCols = coverageColumns(levelName).filter((c) => c.key !== 'notRegistered');
  const gapCols: Column<IdRow>[] = [
    { key: 'name', label: levelName, value: (r) => areaName(r.name) },
    { key: 'notRegistered', label: 'Not registered', value: (r) => r.notRegistered, kind: 'num', color: C.orange },
    {
      key: 'gapRate', label: 'Gap rate', value: (r) => (r.ids ? (r.notRegistered / r.ids) * 100 : 0), kind: 'percent',
      total: (rs) => { const i = rs.reduce((s, r) => s + r.ids, 0); return i ? (rs.reduce((s, r) => s + r.notRegistered, 0) / i) * 100 : 0; },
    },
    {
      key: 'shareOfGap', label: 'Share of gap', value: (r) => (totalNotReg ? (r.notRegistered / totalNotReg) * 100 : 0), kind: 'percent',
      total: (rs) => (totalNotReg ? (rs.reduce((s, r) => s + r.notRegistered, 0) / totalNotReg) * 100 : 0),
    },
    { key: 'ids2', label: 'ID holders', value: (r) => r.ids, kind: 'num', color: C.blue, optional: true },
    { key: 'registered2', label: 'Registered', value: (r) => r.registered, kind: 'num', color: C.green, optional: true },
  ];
  const click = s.canDrill ? drill : undefined;

  return (
    <Section id="not-registered" icon={<PersonOffIcon />} title="With IDs, not registered" accent={C.orange}
      subtitle={`${s.scope} · ID holders who are not on the voter roll`}>
      <NrbFilterBar s={s} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Panel title="IDs" sx={{ height: 480 }}>
            <Loading on={rows.fetching} />
            <DataTable rows={rows.rows} columns={idCols} rowKey={(r) => r.key} onRowClick={click} maxHeight={390}
              fileName="ids" searchPlaceholder={`Search ${levelName.toLowerCase()}…`} emptyText={rows.loading ? 'Loading…' : 'No rows'} />
          </Panel>
        </Grid>
        <Grid item xs={12} md={6}>
          <Panel title="With IDs, not registered" sx={{ height: 480 }}>
            <Loading on={rows.fetching} />
            <DataTable rows={rows.rows} columns={gapCols} rowKey={(r) => r.key} onRowClick={click} maxHeight={390}
              fileName="with-ids-not-registered" searchPlaceholder={`Search ${levelName.toLowerCase()}…`} emptyText={rows.loading ? 'Loading…' : 'No rows'} />
          </Panel>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <IdBreakdowns s={s} measure0="notRegistered" />
      </Box>
    </Section>
  );
};
